import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useConversas } from '@/conversas/ConversasProvider';
import { formatarTelefone } from '@/lib/telefone';
import { ajudaDoNomeNoChat, espelhaNoLead, nomeDoContato, temNomeSalvo } from '@/conversas/contato';
import { useAuth } from '@/auth/AuthProvider';
import {
  atualizarConversa,
  definirEtiquetas,
  vincularLeadConversa,
} from '@/services/conversasService';
import { renomearLead } from '@/services/leadsService';
import FichaLead from '@/components/leads/FichaLead';
import DialogoEtapa from '@/components/leads/DialogoEtapa';
import DialogoVincularLead from '@/components/conversas/DialogoVincularLead';
import { rotuloDoAtendimento } from '@/conversas/estadoAtendimento';
import NomeEditavel from '@/components/NomeEditavel';
import { tratarErro } from '@/lib/erros';

/** Painel do lead ou do paciente, ao lado da conversa (PLANO 5.3). */
export default function PainelContato({ aoFechar }) {
  const { clinicaId, conversa, etiquetas, atendente, clinica, equipe } = useConversas();
  const { papel } = useAuth();
  const [lead, setLead] = useState(null);
  const [paciente, setPaciente] = useState(null);
  const [erro, setErro] = useState(null);
  // Ações do lead a partir da conversa (V2). `ocupado` trava os controles
  // durante uma gravação; `pendente` é a etapa que exige campo extra
  // (fechado/perdido) coletado pelo DialogoEtapa; `vinculando` abre a busca.
  const [ocupado, setOcupado] = useState(false);
  const [pendente, setPendente] = useState(null);
  const [vinculando, setVinculando] = useState(false);

  // O `atendente` do inbox tem a mesma forma do `autor` do funil ({ id, nome }).
  const autor = atendente;
  // VX: quem pode LER leads (rules `podeVendas`) vê a ficha; a recepção vê só um aviso.
  const podeVerLead = papel === 'gestor' || papel === 'sdr' || papel === 'superadmin';

  // Trocar de conversa fecha diálogos e destrava: um "Vincular" pendurado
  // gravaria na conversa errada.
  useEffect(() => {
    setPendente(null);
    setVinculando(false);
    setOcupado(false);
  }, [conversa?.id]);

  useEffect(() => {
    setLead(null);
    if (!conversa?.leadId || !podeVerLead) return undefined;
    return onSnapshot(
      doc(db, 'clinicas', clinicaId, 'leads', conversa.leadId),
      (snap) => setLead(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      () => setErro('Não foi possível carregar o lead vinculado.'),
    );
  }, [clinicaId, conversa?.leadId, podeVerLead]);

  useEffect(() => {
    setPaciente(null);
    if (!conversa?.pacienteId) return undefined;
    return onSnapshot(
      doc(db, 'clinicas', clinicaId, 'pacientes', conversa.pacienteId),
      (snap) => setPaciente(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      // ★★ WT: era `() => {}` — este `onSnapshot` é DIRETO (não passa por serviço), então
      // nem o registro acontecia. ⚠ Sem aviso na tela de propósito: o painel do paciente é
      // contexto extra, e a conversa funciona sem ele.
      tratarErro('a ficha do paciente', null),
    );
  }, [clinicaId, conversa?.pacienteId]);

  if (!conversa) return null;

  async function alternarEtiqueta(id) {
    const atuais = conversa.etiquetas ?? [];
    const proximas = atuais.includes(id) ? atuais.filter((e) => e !== id) : [...atuais, id];
    try {
      await definirEtiquetas(clinicaId, conversa.id, proximas);
    } catch (e) {
      setErro(e.message ?? 'Não foi possível atualizar as etiquetas.');
    }
  }

  /**
   * ★ VN — o nome se salva SÓ AQUI, no submit (nunca em efeito: `renomearLead`
   * escreve nas conversas, e um efeito reagindo ao `onSnapshot` viraria laço).
   *
   * Com `leadId` E permissão de vendas, o caminho é UM só — `renomearLead`, o
   * mesmo da ficha: `lead.nome` + `contatoNomeSalvo` das conversas de VENDAS do
   * lead + a interação "Nome alterado de X para Y". Se esta conversa não é de
   * vendas (gestor numa conversa de recepção com lead), o lote não a cobre e ela
   * recebe o nome à parte. Sem `leadId`, ou sem permissão (recepcionista: as
   * rules não a deixam escrever em `leads`), grava só esta conversa. Nome VAZIO
   * nunca chega ao lead: limpa só o nome salvo da conversa (lead sem nome é
   * recusado).
   */
  async function salvarNome(novo) {
    try {
      if (novo && espelhaNoLead(conversa, papel)) {
        await renomearLead(clinicaId, { id: conversa.leadId, nome: lead?.nome ?? '' }, novo, autor);
        if (conversa.espaco !== 'vendas') await atualizarConversa(clinicaId, conversa.id, { contatoNomeSalvo: novo });
        return;
      }
      await atualizarConversa(clinicaId, conversa.id, { contatoNomeSalvo: novo });
    } catch (e) {
      tratarErro('salvar o nome do contato', null, 'callable')(e);
      throw new Error(e?.message?.startsWith('Informe') ? e.message : 'Não foi possível salvar o nome do contato.');
    }
  }

  async function executar(acao) {
    setErro(null);
    setOcupado(true);
    try {
      await acao();
    } catch (e) {
      console.error('[OdontoSDR] Ação do lead falhou:', e);
      setErro(e.message ?? 'Não foi possível concluir a ação.');
    } finally {
      setOcupado(false);
    }
  }

  /**
   * "Criar lead" e "Vincular a lead existente" passam pela callable: as rules
   * proíbem o cliente de gravar `leadId` na conversa. O listener de `conversa`
   * traz o `leadId` novo e o efeito acima assina o lead — nada a fazer aqui
   * depois do retorno.
   */
  const criarLead = () =>
    executar(() => vincularLeadConversa({ clinicaId, conversaId: conversa.id }));

  const vincularA = (leadEscolhido) =>
    executar(async () => {
      await vincularLeadConversa({ clinicaId, conversaId: conversa.id, leadId: leadEscolhido.id });
      setVinculando(false);
    });


  return (
    <aside className="painel-contato" aria-label="Dados do contato">
      <header className="painel-cabecalho">
        <div className="painel-identidade">
          {/* VM: o editor virou `NomeEditavel`, compartilhado com o painel do lead.
              Começa com o nome SALVO, não com o do WhatsApp: pré-preencher com o
              pushName faria um Salvar sem alteração "adotar" aquele nome como
              escolha da clínica. Campo vazio LIMPA o nome da clínica e devolve a
              exibição ao pushName — é como se desfaz uma renomeação. */}
          {/* `key` pela conversa: trocar de conversa com o campo aberto deixaria o
              nome de um contato pendurado no formulário de outro — remontar fecha. */}
          <NomeEditavel
            key={conversa.id}
            id="pc-nome"
            nome={nomeDoContato(conversa)}
            valorInicial={conversa.contatoNomeSalvo ?? ''}
            rotulo="Nome do contato"
            placeholder={conversa.contatoNome || 'Nome do contato'}
            ajuda={ajudaDoNomeNoChat(conversa, papel)}
            titulo="Editar o nome deste contato"
            aoSalvar={(novo) => salvarNome(novo)}
          />

          {/* Grupo não tem telefone — `contatoTelefone` é null de propósito.
              Um `tel:` vazio viraria um link que disca para nada. */}
          {conversa.ehGrupo ? (
            <p className="painel-sub">Grupo de WhatsApp</p>
          ) : (
            <p className="painel-sub">
              <a href={`tel:${conversa.contatoTelefone}`}>
                {formatarTelefone(conversa.contatoTelefone)}
              </a>
            </p>
          )}

          {/* Sem isto, o atendente não tem como saber que o nome na tela é o
              que a clínica escolheu, e não o que veio do aparelho do contato. */}
          {temNomeSalvo(conversa) && conversa.contatoNome && (
            <p className="indicador-nota">No WhatsApp: {conversa.contatoNome}</p>
          )}
        </div>

        {aoFechar && (
          <button
            type="button"
            className="btn-fechar"
            onClick={aoFechar}
            aria-label="Fechar o painel do contato"
          >
            ×
          </button>
        )}
      </header>

      <div className="painel-corpo">
        {erro && <p className="erro">{erro}</p>}

        <dl className="dados-lead">
          <dt>Canal</dt>
          <dd>
            {/* ★ A-8.1: 'Evolution' → 'Vendas' — mesmo trio do chip da inbox. */}
            {conversa.numeroPapel === 'marketing_entrada'
              ? 'API Oficial'
              : conversa.numeroPapel === 'recepcao'
                ? 'Recepção'
                : 'Vendas'}
          </dd>
          <dt>Espaço</dt>
          <dd>{conversa.espaco === 'recepcao' ? 'Recepção' : 'Vendas / SDR'}</dd>
          <dt>Atendente</dt>
          {/* VW: encerrada não tem dono — mostra quem encerrou. */}
          <dd>{rotuloDoAtendimento(conversa)}</dd>
        </dl>

        {conversa.referral?.anuncioId && (
          <div className="cartao-anuncio">
            <span className="rotulo-acoes">Veio de anúncio</span>
            <p>{conversa.referral.titulo ?? 'Click-to-WhatsApp'}</p>
            <span className="indicador-nota">Anúncio {conversa.referral.anuncioId}</span>
          </div>
        )}

        {/* ★ VX — a FICHA ÚNICA (a mesma do funil), abaixo do que é DA CONVERSA. A recepcionista
            não lê `leads` pelas rules (`podeVendas`): para ela, só o aviso — nada de rules muda. */}
        {conversa.leadId && !podeVerLead && (
          <section className="painel-lead-acoes">
            <p className="vazio compacto">Lead vinculado — visível para a equipe de vendas.</p>
          </section>
        )}
        {lead && podeVerLead && (
          <section className="painel-lead-acoes">
            <div className="bloco-titulo">
              <h3>Lead</h3>
              <a className="btn-secundario compacto" href={`/funil?lead=${lead.id}`}>
                Abrir no funil
              </a>
            </div>
            <FichaLead
              lead={lead}
              clinicaId={clinicaId}
              autor={autor}
              equipe={equipe ?? []}
              clinica={clinica}
              podeLancar={papel === 'gestor' || papel === 'superadmin'}
              papel={papel}
              aoPedirEtapa={setPendente}
              conversa={conversa}
              abrirTodas={false}
            />
          </section>
        )}

        {paciente && (
          <section>
            <h3 className="secao-titulo">Paciente</h3>
            <dl className="dados-lead">
              <dt>Tratamento</dt>
              <dd>{paciente.tratamento || '—'}</dd>
              <dt>Profissional</dt>
              <dd>{paciente.profissional || '—'}</dd>
            </dl>
          </section>
        )}

        {/* Sem lead numa conversa de VENDAS 1:1: a SDR cria ou vincula sem sair
            daqui. Grupo não vira lead (várias pessoas) e a recepção não tem
            funil — nesses casos só o aviso. */}
        {!lead && conversa.espaco === 'vendas' && !conversa.ehGrupo && (
          <section className="painel-sem-lead">
            <p className="vazio compacto">Sem lead vinculado.</p>
            <div className="botoes-tentativa">
              <button
                type="button"
                className="btn-primario compacto"
                disabled={ocupado}
                onClick={criarLead}
              >
                {ocupado ? 'Aguarde…' : 'Criar lead'}
              </button>
              <button
                type="button"
                className="btn-secundario compacto"
                disabled={ocupado}
                onClick={() => setVinculando(true)}
              >
                Vincular a lead existente
              </button>
            </div>
          </section>
        )}
        {!lead && !paciente && (conversa.espaco !== 'vendas' || conversa.ehGrupo) && (
          <p className="vazio compacto">
            {conversa.ehGrupo ? 'Grupo não vira lead nem paciente.' : 'Contato sem paciente vinculado.'}
          </p>
        )}

        <section>
          <h3 className="secao-titulo">Etiquetas</h3>
          <div className="etiquetas-escolha">
            {etiquetas.map((e) => {
              const marcada = (conversa.etiquetas ?? []).includes(e.id);
              return (
                <button
                  key={e.id}
                  type="button"
                  className={marcada ? 'chip-etiqueta marcada' : 'chip-etiqueta'}
                  style={marcada ? { background: e.cor, borderColor: e.cor } : undefined}
                  onClick={() => alternarEtiqueta(e.id)}
                >
                  {e.nome}
                </button>
              );
            })}
            {etiquetas.length === 0 && (
              <span className="indicador-nota">Nenhuma etiqueta cadastrada.</span>
            )}
          </div>
        </section>
      </div>

      <DialogoEtapa
        grade={clinica?.config?.agendaAvaliacao}
        config={clinica?.config ?? null}
        pendente={pendente}
        aoFechar={() => setPendente(null)}
        clinicaId={clinicaId}
        autor={autor}
        equipe={equipe ?? []}
      />
      <DialogoVincularLead
        aberto={vinculando}
        aoFechar={() => setVinculando(false)}
        clinicaId={clinicaId}
        conversa={conversa}
        aoEscolher={vincularA}
        ocupado={ocupado}
        config={clinica?.config ?? null}
      />
    </aside>
  );
}
